import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
  Paper,
  Divider,
  Tooltip,
  Grid,
  Fab,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Publish,
  Archive,
  Edit,
} from '@mui/icons-material';

import { useAuth } from '../../../context/AuthContext';
import { useCentros } from '../../../hooks/useCentros';
import { useFormatos } from '../../../hooks/useFormatos';
import { useFestivos } from '../../../hooks/useFestivos';
import { useGuardiasCuadros, useGuardiasTurnos } from '../../../hooks/useGuardias';
import TurnoFormDialog from '../../../components/guardias/TurnoFormDialog';
import AsignacionesDrawer from '../../../components/guardias/AsignacionesDrawer';
import IncidenciasList from '../../../components/guardias/IncidenciasList';

/* Utilidad: lista de sábados y domingos de un mes (YYYY, 1..12) */
function weekendsOfMonth(y: number, m1_12: number) {
  const first = dayjs()
    .year(y)
    .month(m1_12 - 1)
    .startOf('month');
  const last = first.endOf('month');
  const out: { sab: string; dom: string }[] = [];
  let d = first.startOf('week'); // arranca lunes si tu lib está configurada, si no ajusta

  while (d.isBefore(last, 'day') || d.isSame(last, 'day')) {
    // encuentra el sábado de esa semana
    const saturday = d.day() <= 6 ? d.day(6) : d.add(1, 'week').day(6);
    const sunday = saturday.add(1, 'day');

    if (saturday.isBetween(first.subtract(1, 'day'), last.add(1, 'day'), 'day', '[]')) {
      out.push({
        sab: saturday.format('YYYY-MM-DD'),
        dom: sunday.format('YYYY-MM-DD'),
      });
    }

    d = d.add(1, 'week');
  }

  const uniq = new Map<string, { sab: string; dom: string }>();
  out.forEach((w) => uniq.set(w.sab, w));
  return Array.from(uniq.values()).sort((a, b) => a.sab.localeCompare(b.sab));
}

export default function CuadroMensualPage() {
  const { user } = useAuth();
  const isGestor = (user?.roles ?? []).some((r: string) => ['admin', 'hr'].includes(r)) || false;

  const [month, setMonth] = useState(dayjs().startOf('month')); // mes visible
  const y = month.year();
  const m = month.month() + 1;

  const {
    rows: cuadros,
    loading: loadingC,
    refresh: refCuadros,
    create: createCuadro,
    publicar,
    archivar,
  } = useGuardiasCuadros();

  useEffect(() => {
    refCuadros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cuadroActual = useMemo(
    () => cuadros.find((c) => c.anio === y && c.mes === m),
    [cuadros, y, m],
  );

  const {
    rows: turnos,
    refresh: refTurnos,
    create,
    update,
    publicar: publicarTurno,
    cancelar: cancelarTurno,
    remove,
  } = useGuardiasTurnos(cuadroActual?.id);

  useEffect(() => {
    if (cuadroActual) refTurnos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuadroActual?.id]);

  const weekends = useMemo(() => weekendsOfMonth(y, m), [y, m]);

  /* Festivos del mes que NO caen en fin de semana */
  const { rows: festivosAll } = useFestivos(y);
  const festivosDelMes = useMemo(() => {
    const prefix = `${y}-${String(m).padStart(2, '0')}`;
    return festivosAll
      .filter((f) => {
        if (!f.fecha.startsWith(prefix)) return false;
        const dow = dayjs(f.fecha).day(); // 0=Dom, 6=Sáb
        return dow !== 0 && dow !== 6; // excluir los que ya salen como finde
      })
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [festivosAll, y, m]);

  const [dlg, setDlg] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    initial?: any;
    turnoId?: number;
  }>({ open: false, mode: 'create' });

  const [drawer, setDrawer] = useState<{
    open: boolean;
    turno: { id: number; capacidad: number } | null;
  }>({ open: false, turno: null });

  const goto = (delta: number) => setMonth((p) => p.add(delta, 'month'));

  const crearCuadroSiNoExiste = async () => {
    if (cuadroActual) return;
    await createCuadro({ anio: y, mes: m });
    await refCuadros();
  };

  const onCreateTurno = async (dto: any) => {
    if (!cuadroActual) return;
    // evita pasar "publicar" al endpoint de creación
    const { publicar: doPublish, ...payload } = dto || {};
    const created = await create({ ...payload, cuadroId: cuadroActual.id });
    await refTurnos();
    if (doPublish) {
      await publicarTurno(created.id);
      await refTurnos();
    }
  };

  return (
    <Stack spacing={2}>
      {/* Header navegación mes */}
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton onClick={() => goto(-1)}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
          {month.format('MMMM YYYY')}
        </Typography>
        <IconButton onClick={() => goto(+1)}>
          <ChevronRight />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        {!cuadroActual && (
          <Button variant="contained" onClick={crearCuadroSiNoExiste} disabled={loadingC}>
            Crear cuadro del mes
          </Button>
        )}

        {cuadroActual && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Estado: ${cuadroActual.estado}`}
              color={
                cuadroActual.estado === 'publicado'
                  ? 'success'
                  : cuadroActual.estado === 'borrador'
                    ? 'default'
                    : 'warning'
              }
            />
            {isGestor && (
              <>
                {cuadroActual.estado !== 'publicado' && (
                  <Tooltip title="Publicar cuadro">
                    <IconButton
                      color="primary"
                      onClick={() => publicar(cuadroActual.id).then(refCuadros)}
                    >
                      <Publish />
                    </IconButton>
                  </Tooltip>
                )}
                {cuadroActual.estado !== 'archivado' && (
                  <Tooltip title="Archivar cuadro">
                    <IconButton
                      color="warning"
                      onClick={() => archivar(cuadroActual.id).then(refCuadros)}
                    >
                      <Archive />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        )}
      </Stack>

      {/* Info estados */}
      {!cuadroActual && (
        <Alert severity="info">
          No existe el cuadro de este mes. Crea uno para empezar a añadir turnos.
        </Alert>
      )}

      {/* Grid de fines de semana */}
      {cuadroActual && (
        <Grid container spacing={2}>
          {weekends.map(({ sab, dom }) => (
            <Grid item xs={12} md={6} key={sab}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1">
                    Sáb {dayjs(sab).format('D MMM')} — Dom {dayjs(dom).format('D MMM')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setDlg({
                        open: true,
                        mode: 'create',
                        initial: { cuadroId: cuadroActual.id, fecha: sab },
                      })
                    }
                  >
                    Nuevo turno (Sáb)
                  </Button>
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Sábado */}
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Sábado {dayjs(sab).format('YYYY-MM-DD')}
                </Typography>
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {turnos
                    .filter((t) => t.fecha === sab)
                    .map((t) => (
                      <TurnoRow
                        key={t.id}
                        t={t}
                        onEdit={() =>
                          setDlg({
                            open: true,
                            mode: 'edit',
                            turnoId: t.id,
                            initial: { ...t },
                          })
                        }
                        onDelete={() => remove(t.id)}
                        onPublicar={() => publicarTurno(t.id)}
                        onCancelar={() => cancelarTurno(t.id)}
                        onAsignaciones={() =>
                          setDrawer({
                            open: true,
                            turno: { id: t.id, capacidad: t.capacidad },
                          })
                        }
                      />
                    ))}
                  {turnos.filter((t) => t.fecha === sab).length === 0 && (
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Sin turnos
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {/* Domingo */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Domingo {dayjs(dom).format('YYYY-MM-DD')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setDlg({
                        open: true,
                        mode: 'create',
                        initial: { cuadroId: cuadroActual.id, fecha: dom },
                      })
                    }
                  >
                    Nuevo turno (Dom)
                  </Button>
                </Stack>
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {turnos
                    .filter((t) => t.fecha === dom)
                    .map((t) => (
                      <TurnoRow
                        key={t.id}
                        t={t}
                        onEdit={() =>
                          setDlg({
                            open: true,
                            mode: 'edit',
                            turnoId: t.id,
                            initial: { ...t },
                          })
                        }
                        onDelete={() => remove(t.id)}
                        onPublicar={() => publicarTurno(t.id)}
                        onCancelar={() => cancelarTurno(t.id)}
                        onAsignaciones={() =>
                          setDrawer({
                            open: true,
                            turno: { id: t.id, capacidad: t.capacidad },
                          })
                        }
                      />
                    ))}
                  {turnos.filter((t) => t.fecha === dom).length === 0 && (
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Sin turnos
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />
                {/* Incidencias del finde (si quieres, filtra por turnoId) */}
                <IncidenciasList />
              </Paper>
            </Grid>
          ))}

          {/* Festivos entre semana */}
          {festivosDelMes.map((festivo) => (
            <Grid item xs={12} md={6} key={festivo.fecha}>
              <Paper
                variant="outlined"
                sx={{ p: 1.5, borderColor: 'warning.main', borderWidth: 2 }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1">
                    🎉 Festivo: {dayjs(festivo.fecha).format('dddd D MMM')}{' '}
                    {festivo.nota && (
                      <Typography component="span" variant="body2" sx={{ opacity: 0.7 }}>
                        — {festivo.nota}
                      </Typography>
                    )}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setDlg({
                        open: true,
                        mode: 'create',
                        initial: { cuadroId: cuadroActual!.id, fecha: festivo.fecha },
                      })
                    }
                  >
                    Nuevo turno
                  </Button>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {dayjs(festivo.fecha).format('YYYY-MM-DD')}
                </Typography>
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {turnos
                    .filter((t) => t.fecha === festivo.fecha)
                    .map((t) => (
                      <TurnoRow
                        key={t.id}
                        t={t}
                        onEdit={() =>
                          setDlg({
                            open: true,
                            mode: 'edit',
                            turnoId: t.id,
                            initial: { ...t },
                          })
                        }
                        onDelete={() => remove(t.id)}
                        onPublicar={() => publicarTurno(t.id)}
                        onCancelar={() => cancelarTurno(t.id)}
                        onAsignaciones={() =>
                          setDrawer({
                            open: true,
                            turno: { id: t.id, capacidad: t.capacidad },
                          })
                        }
                      />
                    ))}
                  {turnos.filter((t) => t.fecha === festivo.fecha).length === 0 && (
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Sin turnos
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />
                <IncidenciasList />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* FAB crear turno genérico */}
      {cuadroActual && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', right: 24, bottom: 24 }}
          onClick={() =>
            setDlg({
              open: true,
              mode: 'create',
              initial: {
                cuadroId: cuadroActual.id,
                fecha: dayjs().format('YYYY-MM-DD'),
              },
            })
          }
        >
          <AddIcon />
        </Fab>
      )}

      <TurnoFormDialog
        open={dlg.open}
        onClose={() => setDlg((p) => ({ ...p, open: false }))}
        mode={dlg.mode}
        initial={dlg.initial}
        onSubmit={async (dto) => {
          if (dlg.mode === 'create') {
            await onCreateTurno(dto);
          } else if (dlg.mode === 'edit' && dlg.turnoId) {
            await update(dlg.turnoId, dto);
            await refTurnos();
          }
        }}
      />

      <AsignacionesDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, turno: null })}
        turno={drawer.turno}
      />
    </Stack>
  );
}

function TurnoRow({
  t,
  onEdit,
  onDelete,
  onPublicar,
  onCancelar,
  onAsignaciones,
}: {
  t: any;
  onEdit: () => void;
  onDelete: () => void;
  onPublicar: () => void;
  onCancelar: () => void;
  onAsignaciones: () => void;
}) {
  const { rows: centros = [] } = useCentros();
  const { rows: formatos = [] } = useFormatos();
  const nombreCentro = centros.find((c: any) => Number(c.id) === Number(t.centroId))?.nombre ?? '—';
  const nombreFormato =
    formatos.find((f: any) => Number(f.id) === Number(t.formatoId))?.nombreFormato ??
    `#${t.formatoId}`;

  return (
    <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip label={`${t.horaInicio}–${t.horaFin}`} />
      <Chip label={nombreFormato} variant="outlined" />
      {t.centroId ? <Chip label={nombreCentro} variant="outlined" /> : null}
      <Chip label={`Cap: ${t.capacidad}`} />
      <Chip
        label={t.estado}
        color={
          t.estado === 'publicado' ? 'success' : t.estado === 'cancelado' ? 'warning' : 'default'
        }
      />

      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title="Asignaciones">
        <Button size="small" variant="outlined" onClick={onAsignaciones}>
          Asignar
        </Button>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton onClick={onEdit}>
          <Edit />
        </IconButton>
      </Tooltip>
      {t.estado !== 'publicado' && (
        <Button size="small" onClick={onPublicar}>
          Publicar
        </Button>
      )}
      {t.estado !== 'cancelado' && (
        <Button size="small" color="warning" onClick={onCancelar}>
          Cancelar
        </Button>
      )}
      {t.estado === 'borrador' && (
        <Button size="small" color="error" onClick={onDelete}>
          Eliminar
        </Button>
      )}
    </Paper>
  );
}
